import { useState } from 'react';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { useTranslation, type SupabaseClient } from '@dukanti/shared';
import {
  buildStorefrontNewsletterSubscribersCsv,
  useStorefrontNewsletterSubscribersForAdmin,
  useSuppressStorefrontNewsletterSubscriberForAdmin,
} from '@dukanti/shared/admin';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';

export interface NewsletterSubscriberPanelProps {
  readonly storeId: string;
  readonly client?: SupabaseClient;
}

/** Existing member-authorized list/export/suppress workflow; no sending or new persistence. */
export function NewsletterSubscriberPanel({
  storeId,
  client = supabase,
}: NewsletterSubscriberPanelProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const subscribers = useStorefrontNewsletterSubscribersForAdmin(client, { storeId });
  const suppression = useSuppressStorefrontNewsletterSubscriberForAdmin(client);
  const [actionFailed, setActionFailed] = useState<'suppression' | 'export' | null>(null);
  const rows = subscribers.data ?? [];
  const dateFormatter = new Intl.DateTimeFormat(lang, { dateStyle: 'medium' });

  function exportCsv(): void {
    if (rows.length === 0 || subscribers.isError || subscribers.isFetching) return;
    setActionFailed(null);
    let objectUrl: string | null = null;
    let link: HTMLAnchorElement | null = null;
    try {
      objectUrl = URL.createObjectURL(
        new Blob(['\uFEFF', buildStorefrontNewsletterSubscribersCsv(rows)], {
          type: 'text/csv;charset=utf-8',
        }),
      );
      link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'newsletter-subscribers.csv';
      document.body.appendChild(link);
      link.click();
    } catch {
      setActionFailed('export');
    } finally {
      link?.remove();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  }

  async function suppress(subscriberId: string): Promise<void> {
    if (suppression.isPending) return;
    setActionFailed(null);
    try {
      await suppression.mutateAsync({ storeId, subscriberId });
      const result = await subscribers.refetch();
      if (result.isError) throw result.error;
    } catch {
      setActionFailed('suppression');
    }
  }

  return (
    <section
      id="newsletter-subscribers"
      dir={dir}
      aria-labelledby="newsletter-subscribers-title"
      className="bg-card border-border mt-6 scroll-mt-6 rounded-xl border p-4 shadow-sm sm:p-8"
      data-testid="newsletter-subscribers"
    >
      <h2
        id="newsletter-subscribers-title"
        className="font-display text-foreground text-start text-xl font-bold"
      >
        {t('admin.storeSettings.newsletterCollection.subscriberTitle')}
      </h2>
      <p className="text-muted-foreground mt-2 text-start text-sm">
        {t('admin.storefrontEngine.newsletterSubscribers.description')}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          className="min-h-11 whitespace-normal text-start"
          disabled={subscribers.isFetching || suppression.isPending}
          onClick={() => void subscribers.refetch()}
          data-testid="newsletter-subscribers-refresh"
        >
          <RefreshCw className="size-4" aria-hidden />
          {t('admin.storefrontEngine.newsletterSubscribers.actions.refresh')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          disabled={
            rows.length === 0 ||
            subscribers.isError ||
            subscribers.isFetching ||
            suppression.isPending
          }
          onClick={exportCsv}
          data-testid="newsletter-subscribers-export"
        >
          <Download className="size-4" aria-hidden />
          {t('admin.storeSettings.newsletterCollection.export')}
        </Button>
      </div>
      {actionFailed ? (
        <p
          role="alert"
          className="text-destructive mt-4 text-sm"
          data-testid="newsletter-subscribers-action-error"
        >
          {t(
            actionFailed === 'suppression'
              ? 'admin.storeSettings.newsletterCollection.suppressionError'
              : 'common.errors.unknown',
          )}
        </p>
      ) : null}
      {subscribers.isLoading ? (
        <p role="status" className="text-muted-foreground mt-5 text-sm">
          {t('admin.storefrontEngine.newsletterSubscribers.loading')}
        </p>
      ) : subscribers.isError ? (
        <p
          role="alert"
          className="text-destructive mt-5 text-sm"
          data-testid="newsletter-subscribers-error"
        >
          {t('admin.storeSettings.newsletterCollection.loadError')}
        </p>
      ) : rows.length === 0 ? (
        <p
          className="text-muted-foreground mt-5 text-sm"
          data-testid="newsletter-subscribers-empty"
        >
          {t('admin.storeSettings.newsletterCollection.empty')}
        </p>
      ) : (
        <ul
          className="divide-border mt-5 divide-y"
          aria-busy={subscribers.isFetching || suppression.isPending}
        >
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex min-w-0 flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`newsletter-subscriber-${row.id}`}
            >
              <div className="min-w-0 space-y-1 text-start">
                <p className="text-muted-foreground text-xs">
                  {t(
                    `admin.storefrontEngine.newsletterSubscribers.contactTypes.${row.contactType}`,
                  )}
                </p>
                <p className="text-foreground break-all text-sm font-medium">
                  <bdi dir="ltr">{row.contactValue}</bdi>
                </p>
                <p className="text-muted-foreground text-xs">
                  {t(`admin.storefrontEngine.newsletterSubscribers.statuses.${row.status}`)}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t('admin.storefrontEngine.newsletterSubscribers.columns.created')}
                  {': '}
                  <time dateTime={row.createdAt}>
                    {dateFormatter.format(new Date(row.createdAt))}
                  </time>
                </p>
              </div>
              {row.status === 'active' ? (
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 max-w-full self-start whitespace-normal text-start sm:self-center"
                  disabled={suppression.isPending || subscribers.isFetching}
                  onClick={() => void suppress(row.id)}
                  data-testid={`newsletter-suppress-${row.id}`}
                >
                  {suppression.isPending && suppression.variables?.subscriberId === row.id ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : null}
                  {t('admin.storeSettings.newsletterCollection.suppress')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
